import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../api';
import ClienteSearch from '../components/ClienteSearch';
import Pagination, { PAGE_SIZE } from '../components/Pagination';
import { Plus, Search, Edit2, Trash2, Ticket, X, MapPin, Luggage, UserCheck, ArrowLeftRight } from 'lucide-react';

// ─── Cliente da reserva: busca no servidor (não baixa a lista inteira) ──────────
function ClientePicker({ selected, onChange }) {
  if (selected) {
    return (
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-2 border-[#0147a0] rounded-lg bg-blue-50/60">
        <div className="w-8 h-8 rounded-full bg-[#0147a0] flex items-center justify-center flex-shrink-0">
          <UserCheck className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{selected.nome}</p>
          {selected.telefone && <p className="text-xs text-slate-500">{selected.telefone}</p>}
        </div>
        <button type="button" onClick={() => onChange(null)} title="Trocar cliente"
          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return <ClienteSearch onSelect={onChange} testId="reserva-cliente-search" placeholder="Digite o nome, telefone ou CPF do cliente..." />;
}

const STATUS_RESERVA = [
  { value: 'confirmada', label: 'Confirmada', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'pendente', label: 'Pendente', cls: 'bg-amber-100 text-amber-700' },
  { value: 'cancelada', label: 'Cancelada', cls: 'bg-red-100 text-red-700' },
];
const FORMAS_PAGAMENTO = ['Pix', 'Cartão', 'Dinheiro', 'Mista', 'Depósito', 'Cortesia', 'A Pagar', 'A Receber'];

const getStatusCls = (s) => STATUS_RESERVA.find(o => o.value === s)?.cls || 'bg-slate-100 text-slate-700';
const getStatusLabel = (s) => STATUS_RESERVA.find(o => o.value === s)?.label || s;

// Regra: Ímpares = sempre Janela | Pares = sempre Corredor
// Display por fileira: [b+1 JAN] [b+2 COR] | corredor | [b+4 COR] [b+3 JAN]
function getSeatType(seatNum) {
  return seatNum % 2 === 1 ? 'Janela' : 'Corredor';
}

function SeatMap({ assentos, selected, onSelect }) {
  const seatMap = {};
  assentos.forEach(s => { seatMap[s.numero] = s; });
  const maxSeat = Math.max(...assentos.map(s => s.numero), 0);
  const numRows = Math.ceil(maxSeat / 4);

  const rows = [];
  for (let i = 0; i < numRows; i++) {
    const b = i * 4;
    rows.push([
      seatMap[b + 1] || null,  // col 0: JAN esq
      seatMap[b + 2] || null,  // col 1: COR esq
      seatMap[b + 4] || null,  // col 2: COR dir
      seatMap[b + 3] || null,  // col 3: JAN dir
    ]);
  }

  const handleClick = (seatNum, isOccupied) => {
    if (isOccupied) return;
    onSelect(seatNum === selected ? '' : seatNum);
  };

  const selectedType = selected ? getSeatType(selected) : null;

  const SeatBtn = ({ seat }) => {
    if (!seat) return (
      <div className="h-8 w-full rounded-md bg-slate-100/50 border border-dashed border-slate-200" />
    );
    const isSel = seat.numero === selected;
    const isOcc = seat.status === 'ocupado';
    return (
      <button
        type="button"
        onClick={() => handleClick(seat.numero, isOcc)}
        title={isOcc
          ? `Assento ${seat.numero} — Ocupado: ${seat.cliente_nome}`
          : `Assento ${seat.numero} — ${getSeatType(seat.numero)}`}
        disabled={isOcc}
        data-testid={`seat-${seat.numero}`}
        className={`w-full h-8 rounded-md text-xs font-bold transition-all duration-150 select-none ${
          isSel
            ? 'bg-amber-400 text-white border-2 border-amber-500 shadow-md'
            : isOcc
            ? 'bg-red-100 text-red-400 border border-red-200 cursor-not-allowed opacity-80'
            : 'bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200 hover:shadow-sm cursor-pointer'
        }`}
      >
        {seat.numero}
      </button>
    );
  };

  const GRID = '1fr 1fr 20px 1fr 1fr';

  return (
    <div className="mt-2">
      {/* ── Carcaça do ônibus ── */}
      <div className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">

        {/* Frente do ônibus */}
        <div className="bg-gradient-to-b from-slate-700 to-slate-600 px-4 pt-3 pb-3 flex items-center gap-3">
          {/* Volante + Motorista */}
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
            <svg viewBox="0 0 36 36" width="30" height="30" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="18" cy="18" r="16"/>
              <circle cx="18" cy="18" r="5" fill="#94a3b8" stroke="none"/>
              <line x1="18" y1="2" x2="18" y2="13"/>
              <line x1="18" y1="23" x2="18" y2="34"/>
              <line x1="2" y1="18" x2="13" y2="18"/>
              <line x1="23" y1="18" x2="34" y2="18"/>
            </svg>
            <span className="text-slate-300 text-[8px] font-semibold uppercase tracking-widest">Motorista</span>
          </div>
          {/* Para-brisa */}
          <div className="flex-1 h-10 bg-slate-500/30 rounded-lg border border-slate-500/40 flex items-center justify-center">
            <span className="text-slate-400 text-[9px] font-medium uppercase tracking-widest">Para-brisa</span>
          </div>
          {/* Porta */}
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
            <div className="w-5 h-10 bg-slate-800/50 rounded-sm border border-slate-500/30 flex items-center justify-center">
              <div className="w-1 h-1 rounded-full bg-slate-500"/>
            </div>
            <span className="text-slate-400 text-[8px] font-medium uppercase tracking-widest">Porta</span>
          </div>
        </div>

        {/* Cabeçalho das colunas */}
        <div className="bg-slate-100 border-b border-slate-200 px-4 py-1.5"
          style={{ display: 'grid', gridTemplateColumns: GRID, gap: '6px' }}>
          {['JAN.', 'COR.', '', 'COR.', 'JAN.'].map((h, i) => (
            <div key={i} className="text-center text-[9px] font-bold text-slate-500 uppercase tracking-wider">{h}</div>
          ))}
        </div>

        {/* Assentos */}
        <div className="px-4 py-3 space-y-1.5 max-h-60 overflow-y-auto">
          {rows.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: GRID, gap: '6px', alignItems: 'center' }}>
              <SeatBtn seat={row[0]} />
              <SeatBtn seat={row[1]} />
              <div className="flex items-center justify-center h-8">
                <div className="w-px h-full rounded-full"
                  style={{ background: 'repeating-linear-gradient(to bottom, #cbd5e1 0, #cbd5e1 4px, transparent 4px, transparent 8px)' }}
                />
              </div>
              <SeatBtn seat={row[2]} />
              <SeatBtn seat={row[3]} />
            </div>
          ))}
        </div>

        {/* Fundo do ônibus */}
        <div className="bg-slate-100 border-t border-slate-200 py-1.5 text-center">
          <span className="text-[8px] font-medium text-slate-400 uppercase tracking-widest">— Fundo do Ônibus —</span>
        </div>
      </div>

      {/* Assento selecionado */}
      {selected && selectedType && (
        <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-center">
          <p className="text-sm font-bold text-amber-700">Assento {selected} selecionado — {selectedType}</p>
          <p className="text-xs text-amber-500 mt-0.5">
            {selectedType === 'Janela' ? 'Vista para a janela' : 'Próximo ao corredor'}
            {' · '}Clique novamente para desmarcar
          </p>
        </div>
      )}

      {/* Legenda */}
      <div className="flex items-center justify-center gap-5 mt-2.5 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 bg-emerald-100 border border-emerald-300 rounded inline-block"/>Disponível</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 bg-red-100 border border-red-200 rounded inline-block"/>Ocupado</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 bg-amber-400 rounded inline-block"/>Selecionado</span>
      </div>
    </div>
  );
}

const emptyForm = { cliente_id: '', viagem_id: '', assento: '', valor: '', status: 'confirmada', observacoes: '', local_embarque: '', local_desembarque: '', vendedor: '', forma_pagamento: 'Pix', status_pagamento: 'pago' };

const STATUS_PAGAMENTO = [
  { value: 'pago', label: 'Pago', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'pendente', label: 'Pendente', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'isento', label: 'Isento', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
];
const getStatusPagamentoCls = (s) => STATUS_PAGAMENTO.find(o => o.value === s)?.cls || 'bg-slate-100 text-slate-600 border-slate-300';
const getStatusPagamentoLabel = (s) => STATUS_PAGAMENTO.find(o => o.value === s)?.label || s;

export default function Reservas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = user?.role !== 'financeiro';
  const [reservas, setReservas] = useState([]);
  const [totalReservas, setTotalReservas] = useState(0);
  const [page, setPage] = useState(1);
  const [clienteSel, setClienteSel] = useState(null);
  const [viagens, setViagens] = useState([]);
  const [assentos, setAssentos] = useState([]);
  const [viagemFilter, setViagemFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingSeats, setLoadingSeats] = useState(false);
  const [transferId, setTransferId] = useState(null);
  const [transferReserva, setTransferReserva] = useState(null);
  const [transferForm, setTransferForm] = useState({ viagem_id: '', assento: '', valor: '' });
  const [transferAssentos, setTransferAssentos] = useState([]);
  const [loadingTransferSeats, setLoadingTransferSeats] = useState(false);
  const [savingTransfer, setSavingTransfer] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // O cliente da reserva é buscado no servidor enquanto se digita (ClienteSearch); nenhuma lista de clientes é baixada aqui
      const [rRes, vRes] = await Promise.all([
        api.get('/reservas', {
          params: { viagem_id: viagemFilter || undefined, status: statusFilter || undefined, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }
        }),
        // Mostra viagens agendadas E em andamento — ambas permitem novas reservas
        api.get('/viagens')
      ]);
      const count = Number(rRes.headers['x-total-count']) || 0;
      // Excluiu o último item da última página: volta uma página
      if (rRes.data.length === 0 && page > 1 && count > 0) { setPage(Math.max(1, Math.ceil(count / PAGE_SIZE))); return; }
      setReservas(rRes.data);
      setTotalReservas(count);
      setViagens(vRes.data);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [viagemFilter, statusFilter, page]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadSeats = async (viagemId) => {
    if (!viagemId) { setAssentos([]); return; }
    setLoadingSeats(true);
    try {
      const { data } = await api.get(`/viagens/${viagemId}/assentos`);
      const filtered = editingId ? data.assentos.filter(s => s.status === 'disponivel' || s.reserva_id === editingId) : data.assentos;
      setAssentos(filtered);
    } catch { toast.error('Erro ao carregar assentos'); }
    finally { setLoadingSeats(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setClienteSel(null);
    setAssentos([]);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      cliente_id: r.cliente_id, viagem_id: r.viagem_id, assento: r.assento,
      valor: r.valor || '', status: r.status, observacoes: r.observacoes || '',
      local_embarque: r.local_embarque || '', local_desembarque: r.local_desembarque || '',
      vendedor: r.vendedor || '', forma_pagamento: r.forma_pagamento || 'Pix',
      status_pagamento: r.status_pagamento || 'pago',
    });
    loadSeats(r.viagem_id);
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(emptyForm); setClienteSel(null); setAssentos([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) { toast.error('Selecione um cliente'); return; }
    if (!form.assento) { toast.error('Selecione um assento'); return; }
    setSaving(true);
    try {
      const payload = { ...form, assento: parseInt(form.assento), valor: form.valor ? parseFloat(form.valor) : null };
      if (editingId) {
        await api.put(`/reservas/${editingId}`, {
          assento: payload.assento, valor: payload.valor, status: payload.status,
          observacoes: payload.observacoes, local_embarque: payload.local_embarque,
          local_desembarque: payload.local_desembarque, vendedor: payload.vendedor,
          forma_pagamento: payload.forma_pagamento, status_pagamento: payload.status_pagamento,
        });
        toast.success('Reserva atualizada!');
      } else {
        await api.post('/reservas', payload);
        toast.success('Reserva criada com sucesso!');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar reserva');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/reservas/${deleteId}`);
      toast.success('Reserva excluída!');
      setDeleteId(null);
      fetchAll();
    } catch { toast.error('Erro ao excluir reserva'); }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openTransfer = (r) => {
    setTransferReserva(r);
    setTransferId(r.id);
    setTransferForm({ viagem_id: '', assento: '', valor: r.valor != null ? String(r.valor) : '' });
    setTransferAssentos([]);
  };

  const closeTransfer = () => {
    setTransferId(null);
    setTransferReserva(null);
    setTransferForm({ viagem_id: '', assento: '', valor: '' });
    setTransferAssentos([]);
  };

  const loadTransferSeats = async (viagemId) => {
    if (!viagemId) { setTransferAssentos([]); return; }
    setLoadingTransferSeats(true);
    try {
      const { data } = await api.get(`/viagens/${viagemId}/assentos`);
      setTransferAssentos(data.assentos.filter(s => s.status === 'disponivel'));
    } catch { toast.error('Erro ao carregar assentos'); }
    finally { setLoadingTransferSeats(false); }
  };

  const handleTransfer = async () => {
    if (!transferForm.viagem_id) { toast.error('Selecione a viagem de destino'); return; }
    if (!transferForm.assento) { toast.error('Selecione um assento'); return; }
    setSavingTransfer(true);
    try {
      const payload = { viagem_id: transferForm.viagem_id, assento: parseInt(transferForm.assento) };
      if (transferForm.valor !== '') payload.valor = parseFloat(transferForm.valor);
      await api.post(`/reservas/${transferId}/transferir`, payload);
      toast.success('Passageiro transferido com sucesso!');
      closeTransfer();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao transferir reserva');
    } finally { setSavingTransfer(false); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="reservas-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Reservas</h1>
          <p className="text-slate-500 text-sm mt-0.5">{totalReservas} reserva(s) encontrada(s)</p>
        </div>
        {canWrite && (
        <button onClick={openCreate} data-testid="btn-nova-reserva"
          className="flex items-center gap-2 bg-[#d72d21] hover:bg-[#b91c1c] text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm">
          <Plus className="w-4 h-4" /> Nova Reserva
        </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={viagemFilter} onChange={(e) => { setViagemFilter(e.target.value); setPage(1); }} data-testid="reservas-viagem-filter"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white">
            <option value="">Todas as viagens</option>
            {viagens.map(v => <option key={v.id} value={v.id}>{v.titulo} — {v.destino}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} data-testid="reservas-status-filter"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white">
            <option value="">Todos os status</option>
            {STATUS_RESERVA.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : reservas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Ticket className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhuma reserva encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Assento</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Viagem</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Valor</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Pagamento</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reservas.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`reserva-row-${r.id}`}>
                    <td className="px-5 py-3.5">
                      <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 font-bold text-sm flex items-center justify-center">
                        {r.assento}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-slate-800">{r.cliente?.nome || '—'}</p>
                      <p className="text-xs text-slate-400">{r.cliente?.telefone || ''}</p>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      {r.viagem ? (
                        <div>
                          <p className="font-medium text-slate-700 text-xs">{r.viagem.titulo}</p>
                          <div className="flex items-center gap-1 text-slate-400 text-xs mt-0.5">
                            <MapPin className="w-3 h-3" />
                            <span>{r.viagem.origem} → {r.viagem.destino}</span>
                          </div>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 hidden lg:table-cell text-sm">
                      {r.valor ? `R$ ${r.valor.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusCls(r.status)}`}>
                        {getStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 hidden xl:table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getStatusPagamentoCls(r.status_pagamento)}`}>
                        {getStatusPagamentoLabel(r.status_pagamento || 'pago')}
                      </span>
                      {r.forma_pagamento && (
                        <p className="text-xs text-slate-400 mt-0.5">{r.forma_pagamento}</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/bagagens?reserva_id=${r.id}`)}
                          data-testid={`btn-bagagem-reserva-${r.id}`}
                          title="Registrar Bagagem"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                          <Luggage className="w-4 h-4" />
                        </button>
                        <button onClick={() => openEdit(r)} data-testid={`btn-edit-reserva-${r.id}`}
                          className={`p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => openTransfer(r)} title="Transferir para outra viagem"
                          data-testid={`btn-transfer-reserva-${r.id}`}
                          className={`p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                          <ArrowLeftRight className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(r.id)} data-testid={`btn-delete-reserva-${r.id}`}
                          className={`p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} total={totalReservas} onChange={setPage} disabled={loading} />
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reserva-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingId ? 'Editar Reserva' : 'Nova Reserva'}
              </h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {!editingId && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Viagem *</label>
                    <select value={form.viagem_id} onChange={(e) => { f('viagem_id', e.target.value); f('assento', ''); loadSeats(e.target.value); }} required
                      data-testid="reserva-viagem-select"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white">
                      <option value="">Selecione a viagem</option>
                      {viagens.map(v => <option key={v.id} value={v.id}>{v.titulo} — {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')} ({v.assentos_livres} livres)</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Cliente *</label>
                    <ClientePicker
                      selected={clienteSel}
                      onChange={(c) => { setClienteSel(c); f('cliente_id', c ? c.id : ''); }}
                    />
                  </div>
                </>
              )}

              {/* Seat Map */}
              {form.viagem_id && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Selecione o Assento *
                    {form.assento ? <span className="ml-2 text-orange-500">Selecionado: #{form.assento}</span> : ''}
                  </label>
                  {loadingSeats ? (
                    <div className="p-4 text-center text-slate-400 text-xs">Carregando mapa de assentos...</div>
                  ) : (
                    <SeatMap assentos={assentos} selected={form.assento} onSelect={(n) => f('assento', n)} />
                  )}
                  <input type="hidden" value={form.assento} required data-testid="reserva-assento-input" />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Valor (R$)</label>
                  <input type="number" step="0.01" min="0" value={form.valor} onChange={(e) => f('valor', e.target.value)}
                    placeholder="0,00" data-testid="reserva-valor-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select value={form.status} onChange={(e) => f('status', e.target.value)} data-testid="reserva-status-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white">
                    {STATUS_RESERVA.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Local de Embarque *</label>
                  <input value={form.local_embarque} onChange={(e) => f('local_embarque', e.target.value)} required
                    placeholder="Ex: Rodoviária, Praça Central..." data-testid="reserva-embarque-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Local de Desembarque *</label>
                  <input value={form.local_desembarque} onChange={(e) => f('local_desembarque', e.target.value)} required
                    placeholder="Ex: Terminal, Hotel..." data-testid="reserva-desembarque-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Vendedor *</label>
                  <input value={form.vendedor} onChange={(e) => f('vendedor', e.target.value)} required
                    placeholder="Nome do vendedor" data-testid="reserva-vendedor-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Forma de Pagamento *</label>
                  <select value={form.forma_pagamento} onChange={(e) => f('forma_pagamento', e.target.value)} required
                    data-testid="reserva-pagamento-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white">
                    {FORMAS_PAGAMENTO.map(fp => <option key={fp} value={fp}>{fp}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Status do Pagamento</label>
                <div className="flex gap-2">
                  {STATUS_PAGAMENTO.map(sp => (
                    <button key={sp.value} type="button"
                      onClick={() => f('status_pagamento', sp.value)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold border transition-all ${
                        form.status_pagamento === sp.value
                          ? sp.cls + ' ring-2 ring-offset-1 ring-current'
                          : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400'
                      }`}>
                      {sp.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
                <textarea value={form.observacoes} onChange={(e) => f('observacoes', e.target.value)}
                  rows={2} data-testid="reserva-obs-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} data-testid="btn-salvar-reserva"
                  className="flex-1 bg-[#d72d21] hover:bg-[#b91c1c] text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60">
                  {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Confirmar Reserva')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {transferId && transferReserva && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Transferir Passageiro
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">{transferReserva.cliente?.nome}</p>
              </div>
              <button onClick={closeTransfer} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Viagem atual */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Viagem atual</p>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 font-bold text-sm flex items-center justify-center flex-shrink-0">
                    {transferReserva.assento}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{transferReserva.viagem?.titulo || '—'}</p>
                    <p className="text-xs text-slate-400">{transferReserva.viagem?.origem} → {transferReserva.viagem?.destino}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-purple-500">
                  <ArrowLeftRight className="w-5 h-5" />
                  <span className="text-sm font-medium">Transferir para</span>
                </div>
              </div>

              {/* Nova viagem */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nova Viagem *</label>
                <select
                  value={transferForm.viagem_id}
                  onChange={(e) => {
                    setTransferForm(prev => ({ ...prev, viagem_id: e.target.value, assento: '' }));
                    loadTransferSeats(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/20 focus:border-purple-400 bg-white"
                >
                  <option value="">Selecione a viagem de destino</option>
                  {viagens.filter(v => v.id !== transferReserva.viagem_id).map(v => (
                    <option key={v.id} value={v.id}>
                      {v.titulo} — {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')} ({v.assentos_livres} livres)
                    </option>
                  ))}
                </select>
              </div>

              {/* Mapa de assentos */}
              {transferForm.viagem_id && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Assento na nova viagem *
                    {transferForm.assento ? <span className="ml-2 text-orange-500">Selecionado: #{transferForm.assento}</span> : ''}
                  </label>
                  {loadingTransferSeats ? (
                    <div className="p-4 text-center text-slate-400 text-xs">Carregando mapa de assentos...</div>
                  ) : (
                    <SeatMap
                      assentos={transferAssentos}
                      selected={transferForm.assento}
                      onSelect={(n) => setTransferForm(prev => ({ ...prev, assento: n }))}
                    />
                  )}
                </div>
              )}

              {/* Valor (opcional) */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Valor (R$) <span className="font-normal text-slate-400">— opcional</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={transferForm.valor}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, valor: e.target.value }))}
                  placeholder="Deixe em branco para manter o valor atual"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/20 focus:border-purple-400"
                />
                {transferReserva.valor != null && (
                  <p className="text-xs text-slate-400 mt-1">Valor atual: R$ {transferReserva.valor.toFixed(2)}</p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={closeTransfer}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="button" onClick={handleTransfer} disabled={savingTransfer}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60 flex items-center justify-center gap-2">
                  <ArrowLeftRight className="w-4 h-4" />
                  {savingTransfer ? 'Transferindo...' : 'Confirmar Transferência'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Deseja excluir esta reserva permanentemente?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">Cancelar</button>
              <button onClick={handleDelete} data-testid="btn-confirm-delete-reserva"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
